import { z } from 'zod';
import { isE164Shaped } from './phone-shape.js';

export const FORM_FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'select',
  'multiselect',
  'date',
  'checkbox',
  'radio',
  'file',
  'phone',
  'email',
] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

export const FORM_FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
export const MAX_FIELD_PATTERN_LENGTH = 200;
export const DEFAULT_TEXT_MAX_LENGTH = 500;
export const MAX_FIELDS_PER_SCHEMA = 200;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A `file` field's value is the id of the `form_file` attachment the upload
 * created — never a name, a key or a token. The id is checked over the whole
 * array so the issue lands on the field rather than on one of its elements,
 * which is where a screen puts the message.
 */
const ATTACHMENT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isAttachmentId(value: string): boolean {
  return ATTACHMENT_ID_PATTERN.test(value);
}

const fieldKeySchema = z.string().regex(FORM_FIELD_KEY_PATTERN, 'FORM_FIELD_KEY_INVALID');

const optionSchema = z.object({
  value: z.string().min(1).max(120),
  label: z.string().min(1).max(200),
});

const comparableSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

const visibleOnSchema = z.object({ field: fieldKeySchema, equals: comparableSchema });

function compiles(pattern: string): boolean {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}

const patternSchema = z
  .string()
  .max(MAX_FIELD_PATTERN_LENGTH, 'FORM_PATTERN_TOO_LONG')
  .refine(compiles, 'FORM_PATTERN_UNCOMPILABLE');

const textualRulesSchema = z
  .object({
    minLength: z.int().min(0).max(DEFAULT_TEXT_MAX_LENGTH).nullish(),
    maxLength: z.int().min(1).max(20_000).default(DEFAULT_TEXT_MAX_LENGTH),
    pattern: patternSchema.nullish(),
  })
  .prefault({});

const numberRulesSchema = z
  .object({
    min: z.number().nullish(),
    max: z.number().nullish(),
    step: z.number().positive().nullish(),
    integer: z.boolean().default(false),
  })
  .prefault({});

const dateRulesSchema = z
  .object({
    minDate: z.string().regex(ISO_DATE_PATTERN, 'FORM_DATE_INVALID').nullish(),
    maxDate: z.string().regex(ISO_DATE_PATTERN, 'FORM_DATE_INVALID').nullish(),
  })
  .prefault({});

const multiselectRulesSchema = z
  .object({
    minSelected: z.int().min(0).nullish(),
    maxSelected: z.int().min(1).nullish(),
  })
  .prefault({});

const checkboxRulesSchema = z.object({ mustBeTrue: z.boolean().default(false) }).prefault({});

const fileRulesSchema = z
  .object({
    accept: z.array(z.string().min(1).max(120)).max(20).nullish(),
    maxSizeBytes: z.int().min(1).nullish(),
    maxCount: z.int().min(1).max(50).default(1),
  })
  .prefault({});

const emptyRulesSchema = z.object({}).prefault({});

const fieldBase = {
  key: fieldKeySchema,
  label: z.string().min(1).max(200),
  help: z.string().max(500).nullish(),
  placeholder: z.string().max(200).nullish(),
  required: z.boolean().default(false),
  visibleOn: visibleOnSchema.nullish(),
  widthMd: z.int().min(1).max(12).default(12),
  portalVisible: z.boolean().default(true),
  adminOnly: z.boolean().default(false),
};

const textField = z.object({
  ...fieldBase,
  type: z.literal('text'),
  default: z.string().nullish(),
  rules: textualRulesSchema,
});

const textareaField = z.object({
  ...fieldBase,
  type: z.literal('textarea'),
  default: z.string().nullish(),
  rules: textualRulesSchema,
});

const phoneField = z.object({
  ...fieldBase,
  type: z.literal('phone'),
  default: z.string().nullish(),
  rules: textualRulesSchema,
});

const emailField = z.object({
  ...fieldBase,
  type: z.literal('email'),
  default: z.string().nullish(),
  rules: textualRulesSchema,
});

const numberField = z.object({
  ...fieldBase,
  type: z.literal('number'),
  default: z.number().nullish(),
  rules: numberRulesSchema,
});

const dateField = z.object({
  ...fieldBase,
  type: z.literal('date'),
  default: z.string().regex(ISO_DATE_PATTERN, 'FORM_DATE_INVALID').nullish(),
  rules: dateRulesSchema,
});

const checkboxField = z.object({
  ...fieldBase,
  type: z.literal('checkbox'),
  default: z.boolean().nullish(),
  rules: checkboxRulesSchema,
});

const selectField = z.object({
  ...fieldBase,
  type: z.literal('select'),
  default: z.string().nullish(),
  options: z.array(optionSchema).min(1).max(200),
  rules: emptyRulesSchema,
});

export const RADIO_FIELD_DIRECTIONS = ['row', 'column'] as const;
export type RadioFieldDirection = (typeof RADIO_FIELD_DIRECTIONS)[number];

export const RADIO_FIELD_VARIANTS = ['radio', 'toggle'] as const;
export type RadioFieldVariant = (typeof RADIO_FIELD_VARIANTS)[number];

const radioField = z.object({
  ...fieldBase,
  type: z.literal('radio'),
  default: z.string().nullish(),
  options: z.array(optionSchema).min(1).max(200),
  rules: emptyRulesSchema,
  /**
   * Presentation only, chosen in the builder — issue §6. `column` and `radio`
   * are the defaults, so a document from before these keys existed parses to
   * exactly what it already rendered as.
   */
  direction: z.enum(RADIO_FIELD_DIRECTIONS).default('column'),
  variant: z.enum(RADIO_FIELD_VARIANTS).default('radio'),
});

const multiselectField = z.object({
  ...fieldBase,
  type: z.literal('multiselect'),
  default: z.array(z.string()).nullish(),
  options: z.array(optionSchema).min(1).max(200),
  rules: multiselectRulesSchema,
});

const fileField = z.object({
  ...fieldBase,
  type: z.literal('file'),
  default: z.null().optional(),
  rules: fileRulesSchema,
});

export const formFieldSchema = z.discriminatedUnion('type', [
  textField,
  textareaField,
  phoneField,
  emailField,
  numberField,
  dateField,
  checkboxField,
  selectField,
  radioField,
  multiselectField,
  fileField,
]);

export type FormField = z.infer<typeof formFieldSchema>;

export const formSchemaMetaSchema = z
  .object({
    version: z.int().min(0),
    fields: z.array(formFieldSchema).max(MAX_FIELDS_PER_SCHEMA),
  })
  .superRefine((document, ctx) => {
    const byKey = new Map<string, FormField>();
    document.fields.forEach((field, index) => {
      if (byKey.has(field.key)) {
        ctx.addIssue({
          code: 'custom',
          message: 'FORM_FIELD_KEY_DUPLICATE',
          path: ['fields', index, 'key'],
        });
      }
      byKey.set(field.key, field);
    });

    document.fields.forEach((field, index) => {
      const condition = field.visibleOn;
      if (!condition) return;
      const path = ['fields', index, 'visibleOn', 'field'];
      if (condition.field === field.key) {
        ctx.addIssue({ code: 'custom', message: 'FORM_VISIBLE_ON_SELF_REFERENCE', path });
        return;
      }
      const target = byKey.get(condition.field);
      if (!target) {
        ctx.addIssue({ code: 'custom', message: 'FORM_VISIBLE_ON_UNKNOWN_FIELD', path });
        return;
      }
      if (target.visibleOn) {
        ctx.addIssue({ code: 'custom', message: 'FORM_VISIBLE_ON_NOT_ONE_LEVEL', path });
      }
    });
  });

export type FormSchema = z.infer<typeof formSchemaMetaSchema>;

export type FormValue = string | number | boolean | string[] | null;

export type FormData = Record<string, FormValue>;

/**
 * What an input of this field holds when nothing was answered: an unticked
 * box, no choices, no files, no day, no text. The edit form starts an
 * unanswered field from it, and a correction reads an absent answer as it.
 */
export function formFieldEmptyValue(field: Pick<FormField, 'type'>): FormValue {
  switch (field.type) {
    case 'checkbox':
      return false;
    case 'multiselect':
    case 'file':
      return [];
    case 'date':
      return null;
    default:
      return '';
  }
}

/**
 * What the edit form shows for a stored answer. A missing key is the field's
 * default, or its type's empty value; a stored answer is shown as it is, even
 * a null or an empty string, so a default never stands in for a stored blank.
 */
export function formFieldShownValue(field: FormField, stored: unknown): unknown {
  return stored !== undefined ? stored : (field.default ?? formFieldEmptyValue(field));
}

export function isFieldVisible(field: FormField, values: Record<string, unknown>): boolean {
  const condition = field.visibleOn;
  if (!condition) return true;
  const actual = values[condition.field] ?? null;
  return actual === condition.equals;
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  return Array.isArray(value) && value.length === 0;
}

function isMultipleOfStep(value: number, step: number, origin: number): boolean {
  const steps = (value - origin) / step;
  return Math.abs(steps - Math.round(steps)) < 1e-9;
}

type StringLikeSchema = z.ZodString | z.ZodEmail;

function textualValueSchema(
  rules: z.infer<typeof textualRulesSchema>,
  base: StringLikeSchema = z.string(),
): z.ZodType<string> {
  let schema: StringLikeSchema = base.max(rules.maxLength, 'FORM_VALUE_TOO_LONG');
  if (rules.minLength != null) schema = schema.min(rules.minLength, 'FORM_VALUE_TOO_SHORT');
  if (rules.pattern) schema = schema.regex(new RegExp(rules.pattern), 'FORM_VALUE_PATTERN');
  return schema;
}

function numberValueSchema(rules: z.infer<typeof numberRulesSchema>): z.ZodType<number> {
  let schema = z.number();
  if (rules.integer) schema = schema.int('FORM_VALUE_NOT_INTEGER');
  if (rules.min != null) schema = schema.min(rules.min, 'FORM_VALUE_TOO_SMALL');
  if (rules.max != null) schema = schema.max(rules.max, 'FORM_VALUE_TOO_LARGE');
  const step = rules.step;
  if (step == null) return schema;
  return schema.refine(
    (value) => isMultipleOfStep(value, step, rules.min ?? 0),
    'FORM_VALUE_OFF_STEP',
  );
}

function dateValueSchema(rules: z.infer<typeof dateRulesSchema>): z.ZodType<string> {
  const schema = z.string().regex(ISO_DATE_PATTERN, 'FORM_VALUE_NOT_A_DATE');
  const { minDate, maxDate } = rules;
  return schema.superRefine((value, ctx) => {
    if (minDate && value < minDate) {
      ctx.addIssue({ code: 'custom', message: 'FORM_VALUE_BEFORE_MIN_DATE' });
    }
    if (maxDate && value > maxDate) {
      ctx.addIssue({ code: 'custom', message: 'FORM_VALUE_AFTER_MAX_DATE' });
    }
  });
}

function optionValues(options: readonly { value: string }[]): [string, ...string[]] {
  const values = options.map((option) => option.value);
  return values as [string, ...string[]];
}

/**
 * What a caller adds to the rules a form carries. `isPhone` judges a phone
 * answer; left out, an answer is held to E.164's shape alone. The server
 * passes the numbering plan (`isValidE164`); a screen does not, because that
 * needs the phone metadata on a first screen, and its phone field emits
 * nothing else anyway (docs/decisions/0086).
 */
export type FormDataChecks = { isPhone?: (value: string) => boolean };

function fieldValueSchema(field: FormField, checks: FormDataChecks): z.ZodType<FormValue> {
  switch (field.type) {
    case 'text':
    case 'textarea':
      return textualValueSchema(field.rules);
    case 'phone':
      // Stored in E.164, as every telephone is (docs/decisions/0086); the
      // field's own rules still apply to that form.
      return textualValueSchema(
        field.rules,
        z.string().refine(checks.isPhone ?? isE164Shaped, 'FORM_VALUE_NOT_A_PHONE'),
      );
    case 'email':
      return textualValueSchema(field.rules, z.email('FORM_VALUE_NOT_AN_EMAIL'));
    case 'number':
      return numberValueSchema(field.rules);
    case 'date':
      return dateValueSchema(field.rules);
    case 'checkbox': {
      const schema = z.boolean();
      return field.rules.mustBeTrue
        ? schema.refine((value) => value, 'FORM_VALUE_MUST_BE_ACCEPTED')
        : schema;
    }
    case 'select':
    case 'radio':
      return z.enum(optionValues(field.options), 'FORM_VALUE_NOT_AN_OPTION');
    case 'multiselect': {
      let schema = z.array(z.enum(optionValues(field.options), 'FORM_VALUE_NOT_AN_OPTION'));
      const { minSelected, maxSelected } = field.rules;
      if (minSelected != null) schema = schema.min(minSelected, 'FORM_VALUE_TOO_FEW_SELECTED');
      if (maxSelected != null) schema = schema.max(maxSelected, 'FORM_VALUE_TOO_MANY_SELECTED');
      return schema;
    }
    case 'file':
      return z
        .array(z.string())
        .max(field.rules.maxCount, 'FORM_VALUE_TOO_MANY_FILES')
        .refine((ids) => ids.every(isAttachmentId), 'FORM_VALUE_NOT_A_FILE');
  }
}

export const FORM_AUDIENCES = ['admin', 'portal'] as const;
export type FormAudience = (typeof FORM_AUDIENCES)[number];

/**
 * Whether a field exists for an audience at all. The admin sees every field;
 * the portal sees neither a field with `portalVisible: false` nor an
 * `adminOnly` one. Read leniently, so a stored field missing either flag takes
 * its default.
 */
export function isFieldInAudience(
  field: { portalVisible?: boolean | undefined; adminOnly?: boolean | undefined },
  audience: FormAudience,
): boolean {
  if (audience === 'admin') return true;
  return field.portalVisible !== false && field.adminOnly !== true;
}

type CompiledField = { field: FormField; value: z.ZodType<FormValue> };

/**
 * Builds the schema `form_data` is validated with, for one audience. A field
 * outside the audience is not part of the schema: it is never required, and a
 * value sent for it is stripped like any unknown key, so a portal submission
 * can neither be asked for nor smuggle in a value the portal never showed.
 *
 * Every field is checked in one pass, in schema order: a visible required field
 * that is empty reports `FORM_FIELD_REQUIRED`, and any other present value is
 * checked against its own rules. Requiredness and rules are decided together
 * because a Zod refinement is skipped while any property already failed, which
 * would hide a missing value until every other value was fixed.
 *
 * Visibility is `isFieldVisible` over the audience's own values. A field hidden
 * by its condition is neither required nor validated, and its value is dropped.
 * Unknown keys are stripped, so a stale client cannot inject and a document
 * captured under an older version never fails on a field since removed.
 *
 * `audience` has no default: validating a portal submission as `admin` both
 * demands fields the donor never saw and keeps values the portal never showed,
 * so every caller states whom it is validating for.
 */
export function buildFormDataSchema(
  schema: FormSchema,
  audience: FormAudience,
  checks: FormDataChecks = {},
): z.ZodType<FormData> {
  const compiled: CompiledField[] = schema.fields
    .filter((field) => isFieldInAudience(field, audience))
    .map((field) => ({ field, value: fieldValueSchema(field, checks) }));
  const shape = Object.fromEntries(
    compiled.map(({ field }) => [field.key, z.unknown().optional()]),
  );

  return z.object(shape).transform((values, ctx): FormData => {
    const result: FormData = {};
    for (const { field, value: valueSchema } of compiled) {
      if (!isFieldVisible(field, values)) continue;
      const value = values[field.key];
      if (field.required && isEmptyValue(value)) {
        ctx.addIssue({ code: 'custom', message: 'FORM_FIELD_REQUIRED', path: [field.key] });
        continue;
      }
      if (value === undefined || value === null) continue;
      const parsed = valueSchema.safeParse(value);
      if (parsed.success) {
        result[field.key] = parsed.data;
        continue;
      }
      for (const issue of parsed.error.issues) {
        ctx.addIssue({ code: 'custom', message: issue.message, path: [field.key, ...issue.path] });
      }
    }
    return result;
  });
}

function formOptionLabel(
  options: readonly { value: string; label: string }[],
  value: unknown,
): string {
  return options.find((option) => option.value === value)?.label ?? String(value);
}

/**
 * A select, radio, multiselect or checkbox answer as a person reads it back —
 * the option's own label, or the catalogue's yes/no — never the value or the
 * tick `form_data` stores. `readonlyRows` in `packages/ui` renders the screen
 * with it and the worker's donation printout draws a page with it; both call
 * this rather than resolving an option on their own, since the worker may not
 * import `packages/ui`. Read leniently: an option a later publish removed
 * still prints the value it stored, never throws. Every other field type
 * answers `null`, for the caller to render its own way.
 */
export function formChoiceAnswerText(
  field: FormField,
  value: unknown,
  words: { yes: string; no: string },
): string | null {
  switch (field.type) {
    case 'checkbox':
      return typeof value === 'boolean' ? (value ? words.yes : words.no) : String(value);
    case 'select':
    case 'radio':
      return formOptionLabel(field.options, value);
    case 'multiselect':
      return Array.isArray(value)
        ? value.map((item) => formOptionLabel(field.options, item)).join(', ')
        : String(value);
    default:
      return null;
  }
}
